import { observable, runInAction, action, IObservableArray, makeObservable } from "mobx";
import { AlertError, FetchError } from "General/ErrorTypes";
import { ListStore } from "Stores/RESTStore/ListStore";
// const AbortController = window.AbortController;

export function handleStoreState<T extends CoreStore>(state: keyof T = "state") {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return function (target: object, propertyKey: string, descriptor: TypedPropertyDescriptor<any>) {
    const originalMethod = descriptor.value; // save a reference to the original method

    // NOTE: Do not use arrow syntax here. Use a function expression in
    // order to use the correct value of `this` in this method (see notes below)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    descriptor.value = async function (...args: any[]) {
      let result;
      try {
        runInAction(() => {
          this[`${String(state)}Error`] = undefined;
          this[state as string] = "LOADING";
        });
        result = await originalMethod.apply(this, args);
        runInAction(() => {
          this[state as string] = "DONE";
        });
      } catch (err) {
        // avoid abort errors
        if (err.name !== "AbortError") {
          runInAction(() => {
            this[state as string] = "ERROR";
            this[`${String(state)}Error`] = err;
          });
          throw err;
        }
      }

      return result;
    };

    return descriptor;
  };
}

const allStores: CoreStore[] = [];

export function clearAllStores() {
  allStores.forEach((store) => store.reset());
}

export abstract class CoreStore {
  state: StoreState = "RAW";
  stateError: AlertError | FetchError = null;
  identifier: string = "id";
  abortController = new AbortController();

  constructor(options: { omitAllStoresArray: boolean; id: string } = { omitAllStoresArray: false, id: "id" }) {
    makeObservable(this, {
      state: observable,
      stateError: observable,
      reset: action,
    });

    this.identifier = options.id;
    this.reset();
    if (!options.omitAllStoresArray) {
      allStores.push(this);
    }
  }

  reset() {
    if (this.state === "LOADING") {
      this.cancelRequest();
    }
    this.state = "RAW";
    this.stateError = null;
    this.clear();
    this.abortController = new AbortController();
  }

  cancelRequest() {
    this.abortController.abort();
  }

  abstract clear();

  abstract url(id?: number | string): string;
}

export abstract class ModelStore<T> extends CoreStore {
  constructor(protected listStore: ListStore<T> = undefined, identifier = "id") {
    super({ omitAllStoresArray: false, id: identifier });
  }
}

export abstract class ModifyStore<T> extends ModelStore<T> {
  data: T;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  saveResult: any;
  touched: boolean;

  constructor(listStore = undefined, identifier = undefined) {
    super(listStore, identifier);

    makeObservable(this, {
      data: observable.deep,
      saveResult: observable.deep,
      touched: observable,
      update: action,
    });
  }

  update(o: object) {
    if (!this.data) {
      this.clear();
    }
    const buffer = { ...this.data };
    for (const key in o) {
      if (Array.isArray(o[key]) && this.data[key]) {
        (buffer[key] as IObservableArray<object>).replace(o[key]);
      } else {
        buffer[key] = o[key];
      }
    }
    this.data = buffer;
    this.touched = true;
  }

  reset() {
    this.touched = false;
    this.saveResult = undefined;
    super.reset();
  }

  updateList() {
    if (this.listStore) {
      this.listStore.setItem(this.data);
    }
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function handleStoreState_old(target: object, propertyKey: string, descriptor: TypedPropertyDescriptor<any>) {
  const originalMethod = descriptor.value; // save a reference to the original method

  // NOTE: Do not use arrow syntax here. Use a function expression in
  // order to use the correct value of "this" in this method.
  descriptor.value = async function (...args: object[]) {
    let result;
    const state = "state";
    try {
      runInAction(() => {
        this[`${state}Error`] = undefined;
        this[state] = "LOADING";
      });
      result = await originalMethod.apply(this, args);
      runInAction(() => {
        this[state] = "DONE";
      });
    } catch (err) {
      runInAction(() => {
        this[state] = "ERROR";
        this[`${state}Error`] = err;
      });
      throw err;
    }

    return result;
  };

  return descriptor;
}
